import { useState, useEffect, useCallback, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/common';
import { Book, BookType } from '../types';
import {
  getGraphQLClient,
  GET_PUBLISHED_BOOKS,
  GET_TRENDING_BOOKS,
  GET_MY_FAVORITE_BOOKS,
} from '../lib/graphql';
import { BookCard } from '../components/books';

type SortOption = 'trending' | 'recent' | 'popular' | 'favorites';
type BookTypeFilter = BookType | 'all';

export default function BrowsePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [books, setBooks] = useState<Book[]>([]);
  const [favoriteBooks, setFavoriteBooks] = useState<Book[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>('trending');
  const [filterType, setFilterType] = useState<BookTypeFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const LIMIT = 12;

  const loadBooks = useCallback(
    async (reset = false) => {
      setIsLoading(true);
      try {
        const client = getGraphQLClient();
        const currentOffset = reset ? 0 : offset;

        if (sortBy === 'trending') {
          const data = await client.request<{ trendingBooks: Book[] }>(GET_TRENDING_BOOKS, {
            limit: 50,
          });
          setBooks(data.trendingBooks);
          setHasMore(false);
        } else if (sortBy === 'favorites' && user) {
          const data = await client.request<{ myFavoriteBooks: Book[] }>(GET_MY_FAVORITE_BOOKS);
          setBooks(data.myFavoriteBooks);
          setHasMore(false);
        } else {
          const data = await client.request<{ publishedBooks: Book[] }>(GET_PUBLISHED_BOOKS, {
            limit: LIMIT,
            offset: currentOffset,
          });
          if (reset) {
            setBooks(data.publishedBooks);
          } else {
            setBooks((prev) => [...prev, ...data.publishedBooks]);
          }
          setHasMore(data.publishedBooks.length === LIMIT);
          if (!reset) setOffset(currentOffset + LIMIT);
        }

        // Load favorites for the favorites tab
        if (user && sortBy !== 'favorites') {
          const favData = await client.request<{ myFavoriteBooks: Book[] }>(GET_MY_FAVORITE_BOOKS);
          setFavoriteBooks(favData.myFavoriteBooks);
        }
      } catch (error) {
        console.error('Failed to load books:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [sortBy, offset, user]
  );

  useEffect(() => {
    setOffset(0);
    loadBooks(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  const handleFavoriteToggle = (bookId: string, isFavorited: boolean) => {
    setBooks((prev) =>
      prev.map((book) =>
        book.id === bookId
          ? { ...book, isFavorited, favoritesCount: book.favoritesCount + (isFavorited ? 1 : -1) }
          : book
      )
    );

    if (isFavorited) {
      const book = books.find((b) => b.id === bookId);
      if (book && !favoriteBooks.find((b) => b.id === bookId)) {
        setFavoriteBooks((prev) => [{ ...book, isFavorited: true }, ...prev]);
      }
    } else {
      setFavoriteBooks((prev) => prev.filter((b) => b.id !== bookId));
    }
  };

  const filteredBooks = books.filter((book) => {
    const matchesType = filterType === 'all' || book.bookType?.toLowerCase() === filterType;
    const matchesSearch =
      searchQuery === '' ||
      (book.title?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false) ||
      book.summary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.themes?.some((theme) => theme.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesType && matchesSearch;
  });

  const bookTypes: { value: BookTypeFilter; label: string }[] = [
    { value: 'all', label: 'All Types' },
    { value: 'fiction', label: t('bookTypes.fiction') },
    { value: 'non_fiction', label: t('bookTypes.non_fiction') },
  ];

  const sortOptions: { value: SortOption; label: string; icon: ReactElement }[] = [
    {
      value: 'trending',
      label: 'Trending',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
          />
        </svg>
      ),
    },
    {
      value: 'recent',
      label: 'Most Recent',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      value: 'favorites',
      label: 'My Favorites',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className="relative min-h-screen">
      {/* Background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-gradient">Browse Books</span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Discover and explore AI-crafted stories from our community
          </p>
        </motion.div>

        {/* Filters and Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 space-y-4"
        >
          {/* Search Bar */}
          <div className="relative">
            <svg
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search books by title, summary, or theme..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Sort and Filter Row */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            {/* Sort Options */}
            <div className="flex gap-2 flex-wrap">
              {sortOptions
                .filter((option) => option.value !== 'favorites' || user)
                .map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSortBy(option.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                      sortBy === option.value
                        ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25'
                        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {option.icon}
                    {option.label}
                    {option.value === 'favorites' && favoriteBooks.length > 0 && (
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded-full ${
                          sortBy === 'favorites' ? 'bg-white/20' : 'bg-red-500 text-white'
                        }`}
                      >
                        {favoriteBooks.length}
                      </span>
                    )}
                  </button>
                ))}
            </div>

            {/* Type Filter */}
            <div className="flex gap-2 flex-wrap">
              {bookTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setFilterType(type.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    filterType === type.value
                      ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Results Count */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-6 text-gray-600 dark:text-gray-400"
        >
          {filteredBooks.length} {filteredBooks.length === 1 ? 'book' : 'books'} found
        </motion.div>

        {/* Books Grid */}
        <AnimatePresence mode="wait">
          {isLoading && books.length === 0 ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex justify-center items-center py-20"
            >
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </motion.div>
          ) : filteredBooks.length > 0 ? (
            <motion.div
              key="books"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredBooks.map((book, index) => (
                  <motion.div
                    key={book.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(index * 0.03, 0.3) }}
                  >
                    <BookCard
                      book={book}
                      variant="gallery"
                      showStats={true}
                      showFavoriteButton={true}
                      onFavoriteToggle={handleFavoriteToggle}
                    />
                  </motion.div>
                ))}
              </div>

              {/* Load More */}
              {hasMore && sortBy === 'recent' && (
                <div className="text-center mt-10">
                  <Button
                    onClick={() => loadBooks(false)}
                    disabled={isLoading}
                    variant="secondary"
                    size="lg"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Loading...
                      </span>
                    ) : (
                      'Load More Books'
                    )}
                  </Button>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-20"
            >
              <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {sortBy === 'favorites' ? 'No favorites yet' : 'No books found'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {sortBy === 'favorites'
                  ? 'Start exploring and save books you love!'
                  : searchQuery
                    ? 'Try adjusting your search or filters'
                    : 'Be the first to publish a book!'}
              </p>
              {(searchQuery || filterType !== 'all') && (
                <Button
                  onClick={() => {
                    setSearchQuery('');
                    setFilterType('all');
                  }}
                  variant="secondary"
                >
                  Clear Filters
                </Button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
